import { useEffect, useMemo, useState } from 'react';
import type { Incident, RegionSnapshot } from '@wildfire/core';
import { MapView } from './components/MapView.js';
import { Sidebar, type Filters } from './components/Sidebar.js';
import { fetchRegions, fetchSnapshot } from './api.js';

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_FILTERS: Filters = {
  region: 'conus',
  states: [],
  minAcres: 0,
  onlyNew24h: false,
  sort: 'size',
};

type RegionMap = Awaited<ReturnType<typeof fetchRegions>>;

export function App() {
  const [regions, setRegions] = useState<RegionMap | null>(null);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [snapshot, setSnapshot] = useState<RegionSnapshot | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load region metadata once.
  useEffect(() => {
    fetchRegions()
      .then(setRegions)
      .catch((err) => setError((err as Error).message));
  }, []);

  // Fetch snapshot whenever region changes.
  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchSnapshot(filters.region)
      .then((snap) => {
        setSnapshot(snap);
        setSelectedId(null);
      })
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [filters.region]);

  const incidents = useMemo<Incident[]>(() => {
    const base = snapshot?.incidents ?? [];
    const now = Date.now();
    const filtered = base.filter((i) => {
      if (filters.states.length > 0 && !filters.states.includes(i.location.state)) return false;
      if (i.size.acres < filters.minAcres) return false;
      if (filters.onlyNew24h) {
        const discovered = Date.parse(i.discoveredAt);
        if (!Number.isFinite(discovered) || now - discovered > DAY_MS) return false;
      }
      return true;
    });

    switch (filters.sort) {
      case 'size':
        return filtered.sort((a, b) => b.size.acres - a.size.acres);
      case 'date':
        return filtered.sort(
          (a, b) => Date.parse(b.discoveredAt) - Date.parse(a.discoveredAt),
        );
      case 'state':
        return filtered.sort((a, b) => a.location.state.localeCompare(b.location.state));
      default:
        return filtered;
    }
  }, [snapshot, filters]);

  const regionList = useMemo(() => {
    if (!regions) return [];
    return Object.entries(regions).map(([key, value]) => ({
      key,
      name: value.name,
      states: value.states,
    }));
  }, [regions]);

  const activeRegion = regions?.[filters.region];

  return (
    <div className="app">
      <Sidebar
        regions={regionList}
        filters={filters}
        onChange={setFilters}
        snapshot={snapshot}
        incidents={incidents}
        selectedId={selectedId}
        onSelect={setSelectedId}
        loading={loading}
      />
      <div className="map-pane">
        <MapView
          snapshot={snapshot}
          incidents={incidents}
          selectedId={selectedId}
          onSelect={setSelectedId}
          bbox={activeRegion?.bbox ?? null}
        />
        {error ? <div className="status-banner err">{error}</div> : null}
        {!error && snapshot?.warnings?.length ? (
          <div className="status-banner">
            {snapshot.warnings.map((w) => `${w.source}: ${w.message}`).join(' · ')}
          </div>
        ) : null}
        <div className="legend">
          <div className="row">
            <span className="swatch" style={{ background: '#f0d053' }} /> &lt; 100 ac
          </div>
          <div className="row">
            <span className="swatch" style={{ background: '#f6a15c' }} /> 100–1,000 ac
          </div>
          <div className="row">
            <span className="swatch" style={{ background: '#f25c1f' }} /> 1,000–10,000 ac
          </div>
          <div className="row">
            <span className="swatch" style={{ background: '#d11414' }} /> &gt; 10,000 ac
          </div>
        </div>
      </div>
    </div>
  );
}
